import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { Formik } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import useToken from 'src/hooks/useToken';
import { SignInSuccessful } from 'src/redux/actions';
import { setError } from 'src/redux/errorReducer';
import API from 'src/util/api';
import * as Yup from 'yup';

const Login = () => {
  const { setToken } = useToken();
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const handleDialog = (condition) => {
    setOpen(condition === 'open');
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (loginInfo) => {
    try {
      const response = await API.post('newlogin', loginInfo);
      if (response?.success) {
        setToken(response?.token);
        dispatch(SignInSuccessful(response));
      } else {
        handleDialog('open');
      }
    } catch (error) {
      dispatch(setError(error));
    }
  };

  return (
    <>
      <Helmet>
        <title>Login | Langistan</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={6} sx={{ padding: 4, borderRadius: 3 }}>
            <Typography variant="h4" align="center" gutterBottom>
              Sign in to Langistan
            </Typography>

            <Formik
              initialValues={{ email: '', password: '' }}
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .email('Must be a valid email')
                  .max(255)
                  .required('Email is required'),
                password: Yup.string()
                  .max(255)
                  .required('Password is required'),
              })}
              onSubmit={onSubmit}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Email address"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ mt: 3 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Sign In
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Paper>
        </Container>

        <Dialog
          open={open}
          onClose={() => handleDialog('close')}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <Typography variant="h6" color="error">
              Invalid credentials. Please try again.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleDialog('close')} color="primary" autoFocus>
              Okay
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default Login;
